/* FN · Product photo zoom.
   Opens a full-screen viewer from the fn-product gallery (zoom button or a tap on a photo).
   Pinch, double-tap, drag, scroll wheel, buttons and keyboard all zoom so shoppers can read the label.
   Respects the section's "Enable full-screen zoom" setting. */
(function () {
  'use strict';
  if (window.FnZoom) return;

  var MAX = 4;
  var STEP = 2.5;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  var CSS = '' +
    '.fnz{position:fixed;inset:0;width:100%;height:100%;max-width:none;max-height:none;margin:0;padding:0;border:0;background:#fff;color:var(--fn-ink,#151a17);overflow:hidden;font-family:var(--fn-body,inherit)}' +
    '.fnz::backdrop{background:rgba(18,22,20,.5)}' +
    '.fnz[open]{display:block}' +
    '.fnz__stage{position:absolute;inset:0;overflow:hidden;touch-action:none;cursor:zoom-in;-webkit-user-select:none;user-select:none}' +
    '.fnz.is-zoomed .fnz__stage{cursor:grab}' +
    '.fnz.is-drag .fnz__stage{cursor:grabbing}' +
    '.fnz__strip{display:flex;height:100%;will-change:transform}' +
    '.fnz__item{position:relative;flex:0 0 100%;height:100%;overflow:hidden;padding:64px 12px 76px;box-sizing:border-box}' +
    '.fnz__item img{display:block;width:100%;height:100%;object-fit:contain;transform-origin:50% 50%;will-change:transform;pointer-events:none;-webkit-user-drag:none}' +
    '.fnz__anim .fnz__strip,.fnz__anim .fnz__item img{transition:transform .32s cubic-bezier(.2,.8,.2,1)}' +
    '.fnz__bar{position:absolute;z-index:2;top:0;left:0;right:0;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 12px 0 18px;pointer-events:none}' +
    '.fnz__count{font:600 13px/1 var(--fn-body,inherit);font-variant-numeric:tabular-nums;letter-spacing:.02em;padding:9px 12px;border-radius:999px;background:rgba(255,255,255,.85);-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px)}' +
    '.fnz__tools{display:flex;gap:8px;pointer-events:auto}' +
    '.fnz button.fnz__btn{width:44px;height:44px;border-radius:50%;border:0;padding:0;margin:0;display:inline-flex;align-items:center;justify-content:center;background:#fff;color:var(--fn-ink,#151a17);box-shadow:0 10px 24px -14px rgba(18,22,20,.55),0 0 0 1px rgba(18,22,20,.06);cursor:pointer;transition:background-color .2s ease,color .2s ease,opacity .2s ease}' +
    '.fnz button.fnz__btn:hover{background:var(--fn-ink,#151a17);color:#fff}' +
    '.fnz button.fnz__btn[disabled]{opacity:.35;pointer-events:none}' +
    '.fnz button.fnz__btn--close{background:var(--fn-ink,#151a17);color:#fff}' +
    '.fnz button.fnz__nav{position:absolute;z-index:2;top:50%;margin-top:-22px}' +
    '.fnz button.fnz__nav--prev{left:16px}.fnz button.fnz__nav--next{right:16px}' +
    '.fnz button:focus-visible{outline:3px solid var(--fn-accent,#2f5d46);outline-offset:2px}' +
    '.fnz__hint{position:absolute;z-index:2;left:50%;bottom:20px;transform:translateX(-50%);margin:0;padding:10px 16px;border-radius:999px;background:var(--fn-ink,#151a17);color:#fff;font:600 13px/1.2 var(--fn-body,inherit);white-space:nowrap;pointer-events:none;transition:opacity .4s ease}' +
    '.fnz__hint.is-off{opacity:0}' +
    '.fnz__dots{position:absolute;z-index:2;left:50%;bottom:28px;transform:translateX(-50%);display:flex;gap:6px}' +
    '.fnz__dots span{width:6px;height:6px;border-radius:50%;background:rgba(18,22,20,.22);transition:width .25s ease,background-color .25s ease}' +
    '.fnz__dots span.is-on{width:22px;border-radius:6px;background:var(--fn-ink,#151a17)}' +
    '.fnz__hint:not(.is-off)~.fnz__dots{opacity:0}' +
    '.fnz.is-zoomed .fnz__nav,.fnz.is-zoomed .fnz__dots{opacity:0;pointer-events:none}' +
    '@media (max-width:760px){.fnz button.fnz__nav{display:none}.fnz__item{padding:60px 0 64px}}' +
    '@media (prefers-reduced-motion:reduce){.fnz__anim .fnz__strip,.fnz__anim .fnz__item img{transition:none}}' +
    'html.fnz-open{overflow:hidden}';

  var ICON = {
    minus: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 12h12"/></svg>',
    plus: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 6v12M6 12h12"/></svg>',
    x: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
    prev: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M15 6l-6 6 6 6"/></svg>',
    next: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>'
  };

  var ui, stage, strip, countEl, hintEl, dotsEl, btnIn, btnOut, btnPrev, btnNext;
  var list = [];      /* [{slide, img}] for the open product */
  var items = [];     /* viewer item elements */
  var bounds = [];    /* where the product sits inside each photo, 0 to 1 [x0, y0, x1, y1] */
  var cur = 0;
  var root = null;    /* the [data-fnp] element we opened from */
  var opener = null;
  var z = { s: 1, x: 0, y: 0 };
  var base = { s: 1, x: 0, y: 0 };  /* the framed starting view for the current photo */
  var touched = false;
  var pts = {};
  var g = null;
  var lastTap = null;
  var hintTimer = 0;

  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function count() { return Object.keys(pts).length; }
  function two() { var k = Object.keys(pts); return [pts[k[0]], pts[k[1]]]; }
  function maxS() { return Math.max(MAX, base.s * 2.2); }
  function zoomed() { return z.s > base.s + 0.01; }

  function build() {
    if (ui) return;
    var st = document.createElement('style');
    st.textContent = CSS;
    document.head.appendChild(st);

    ui = document.createElement('dialog');
    ui.className = 'fnz';
    ui.innerHTML =
      '<div class="fnz__stage" data-fnz-stage><div class="fnz__strip" data-fnz-strip></div></div>' +
      '<div class="fnz__bar"><span class="fnz__count" aria-live="polite"></span><div class="fnz__tools">' +
        '<button type="button" class="fnz__btn" data-fnz="out" aria-label="Zoom out">' + ICON.minus + '</button>' +
        '<button type="button" class="fnz__btn" data-fnz="in" aria-label="Zoom in">' + ICON.plus + '</button>' +
        '<button type="button" class="fnz__btn fnz__btn--close" data-fnz="close" aria-label="Close">' + ICON.x + '</button>' +
      '</div></div>' +
      '<button type="button" class="fnz__btn fnz__nav fnz__nav--prev" data-fnz="prev" aria-label="Previous image">' + ICON.prev + '</button>' +
      '<button type="button" class="fnz__btn fnz__nav fnz__nav--next" data-fnz="next" aria-label="Next image">' + ICON.next + '</button>' +
      '<p class="fnz__hint" aria-hidden="true"></p>' +
      '<div class="fnz__dots" aria-hidden="true"></div>';
    document.body.appendChild(ui);

    stage = ui.querySelector('[data-fnz-stage]');
    strip = ui.querySelector('[data-fnz-strip]');
    countEl = ui.querySelector('.fnz__count');
    hintEl = ui.querySelector('.fnz__hint');
    dotsEl = ui.querySelector('.fnz__dots');
    btnIn = ui.querySelector('[data-fnz="in"]');
    btnOut = ui.querySelector('[data-fnz="out"]');
    btnPrev = ui.querySelector('[data-fnz="prev"]');
    btnNext = ui.querySelector('[data-fnz="next"]');
    hintEl.textContent = coarse ? 'Pinch or double-tap to zoom' : 'Click or scroll to zoom';

    ui.querySelector('[data-fnz="close"]').addEventListener('click', function () { ui.close(); });
    btnIn.addEventListener('click', function () { zoomBy(zoomed() ? 1.6 : Math.max(STEP, base.s * 1.8) / z.s); });
    btnOut.addEventListener('click', function () { zoomBy(1 / 1.6); });
    btnPrev.addEventListener('click', function () { go(cur - 1); });
    btnNext.addEventListener('click', function () { go(cur + 1); });
    ui.addEventListener('close', onClose);
    ui.addEventListener('keydown', onKey);

    stage.addEventListener('pointerdown', onDown);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerup', onUp);
    stage.addEventListener('pointercancel', onUp);
    stage.addEventListener('wheel', onWheel, { passive: false });
    stage.addEventListener('dragstart', function (e) { e.preventDefault(); });
    /* iOS Safari: keep the page itself from pinch or double-tap zooming behind the viewer. */
    stage.addEventListener('touchmove', function (e) { e.preventDefault(); }, { passive: false });
    ui.addEventListener('gesturestart', function (e) { e.preventDefault(); });
    window.addEventListener('resize', function () { if (ui.open) { reset(false); place(false); } });
  }

  /* ---------- Content ---------- */
  function collect(r) {
    var out = [];
    r.querySelectorAll('[data-fnp-slide]').forEach(function (sl) {
      var img = sl.querySelector('.fnp-frame img');
      if (img) out.push({ slide: Number(sl.getAttribute('data-index')) || 0, img: img });
    });
    return out;
  }

  function fill() {
    strip.innerHTML = '';
    dotsEl.innerHTML = '';
    bounds = [];
    items = list.map(function (entry, i) {
      var it = document.createElement('div');
      it.className = 'fnz__item';
      it.setAttribute('role', 'group');
      it.setAttribute('aria-roledescription', 'slide');
      it.setAttribute('aria-label', (i + 1) + ' of ' + list.length);
      var im = document.createElement('img');
      im.alt = entry.img.getAttribute('alt') || '';
      im.decoding = 'async';
      im.draggable = false;
      if (entry.img.getAttribute('width')) im.setAttribute('width', entry.img.getAttribute('width'));
      if (entry.img.getAttribute('height')) im.setAttribute('height', entry.img.getAttribute('height'));
      im.setAttribute('data-src', entry.img.getAttribute('src') || entry.img.currentSrc || '');
      if (entry.img.getAttribute('srcset')) im.setAttribute('data-srcset', entry.img.getAttribute('srcset'));
      im.addEventListener('load', function () {
        if (i === cur && !touched && ui.open) reset(true);
      });
      it.appendChild(im);
      strip.appendChild(it);
      dotsEl.appendChild(document.createElement('span'));
      return it;
    });
    var many = list.length > 1;
    btnPrev.hidden = btnNext.hidden = !many;
    dotsEl.hidden = !many;
    countEl.hidden = !many;
  }

  function load(i) {
    var it = items[i];
    if (!it) return;
    var im = it.firstChild;
    if (im.getAttribute('data-src')) {
      /* Ask for the largest file the store has so the label stays sharp when zoomed in. */
      im.sizes = '(min-width: 990px) 100vw, 220vw';
      if (im.getAttribute('data-srcset')) im.srcset = im.getAttribute('data-srcset');
      im.src = im.getAttribute('data-src');
      im.removeAttribute('data-src');
      im.removeAttribute('data-srcset');
    }
    probe(i);
  }

  function smallest(ss) {
    var best = null;
    (ss || '').split(',').forEach(function (part) {
      var m = part.trim().split(/\s+/);
      var w = parseInt(m[1], 10) || 0;
      if (m[0] && (!best || w < best.w)) best = { u: m[0], w: w };
    });
    return best ? best.u : '';
  }

  /* Find the product inside a pack shot (anything that is not the plain background),
     so the viewer can open already framed on it. Photos with a busy background are left as they are. */
  function probe(i) {
    if (bounds[i] !== undefined || !list[i]) return;
    bounds[i] = null;
    var src = smallest(list[i].img.getAttribute('srcset')) || list[i].img.currentSrc || list[i].img.getAttribute('src');
    if (!src) return;
    var p = new Image();
    p.crossOrigin = 'anonymous';
    p.onload = function () {
      try { bounds[i] = measure(p); } catch (err) { bounds[i] = null; }
      if (bounds[i] && i === cur && !touched && ui.open) reset(true);
    };
    p.src = src;
  }

  function measure(p) {
    var n = 96;
    var r = p.naturalWidth / p.naturalHeight;
    if (!r) return null;
    var w = r >= 1 ? n : Math.max(8, Math.round(n * r));
    var h = r >= 1 ? Math.max(8, Math.round(n / r)) : n;
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(p, 0, 0, w, h);
    var d = ctx.getImageData(0, 0, w, h).data;
    var corners = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + w - 1) * 4];
    var bg = [0, 0, 0, 0];
    corners.forEach(function (k) { for (var j = 0; j < 4; j++) bg[j] += d[k + j] / 4; });
    var spread = 0;
    corners.forEach(function (k) { for (var j = 0; j < 4; j++) spread = Math.max(spread, Math.abs(d[k + j] - bg[j])); });
    if (spread > 26) return null;
    var clear = bg[3] < 24;
    var x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var k = (y * w + x) * 4;
        var hit = clear ? d[k + 3] > 40 : (d[k + 3] > 40 && Math.max(Math.abs(d[k] - bg[0]), Math.abs(d[k + 1] - bg[1]), Math.abs(d[k + 2] - bg[2])) > 22);
        if (hit) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
    }
    if (x1 < 0) return null;
    var pad = 0.025;
    var b = [Math.max(0, x0 / w - pad), Math.max(0, y0 / h - pad), Math.min(1, (x1 + 1) / w + pad), Math.min(1, (y1 + 1) / h + pad)];
    if (b[2] - b[0] > 0.86 && b[3] - b[1] > 0.86) return null;
    return b;
  }

  /* ---------- Open / close ---------- */
  function open(r, slideIndex, from) {
    build();
    root = r;
    opener = from || null;
    list = collect(r);
    if (!list.length) return;
    /* The gallery can hold videos, so map its slide number to the matching photo (or the next one). */
    var start = list.length - 1;
    for (var i = 0; i < list.length; i++) {
      if (list[i].slide >= slideIndex) { start = i; break; }
    }
    fill();
    var title = r.querySelector('.fnp-title, h1');
    ui.setAttribute('aria-label', (title ? title.textContent.trim() + ' ' : '') + 'images');
    cur = start;
    touched = false;
    ui.showModal();
    document.documentElement.classList.add('fnz-open');
    place(false);
    reset(false);
    showHint();
    ui.querySelector('[data-fnz="close"]').focus({ preventScroll: true });
  }

  function onClose() {
    document.documentElement.classList.remove('fnz-open');
    pts = {}; g = null;
    ui.style.backgroundColor = '';
    var pdp = root && root.__fnp;
    if (pdp && list[cur] && typeof pdp.goTo === 'function' && pdp.index !== list[cur].slide) pdp.goTo(list[cur].slide, true);
    if (opener && document.contains(opener) && opener.focus) opener.focus({ preventScroll: true });
  }

  function showHint() {
    clearTimeout(hintTimer);
    hintEl.classList.remove('is-off');
    hintTimer = setTimeout(hideHint, 2600);
  }
  function hideHint() { clearTimeout(hintTimer); hintEl.classList.add('is-off'); }
  function interact() { touched = true; hideHint(); }

  /* ---------- Layout ---------- */
  function anim(on) { ui.classList.toggle('fnz__anim', !!on && !reduce); }

  function place(animate, dx) {
    anim(animate);
    strip.style.transform = 'translate3d(' + (-cur * stage.clientWidth + (dx || 0)) + 'px,0,0)';
    for (var i = cur - 1; i <= cur + 1; i++) load(i);
    countEl.textContent = (cur + 1) + ' / ' + list.length;
    Array.prototype.forEach.call(dotsEl.children, function (d, i) { d.classList.toggle('is-on', i === cur); });
    btnPrev.disabled = cur <= 0;
    btnNext.disabled = cur >= list.length - 1;
    apply(animate);
  }

  function go(i) {
    i = clamp(i, 0, list.length - 1);
    if (i !== cur) {
      var old = img();
      if (old) old.style.transform = '';
      cur = i;
      touched = false;
      place(true);
      reset(true);
      return;
    }
    place(true);
  }

  function img() { return items[cur] && items[cur].firstChild; }

  /* Size of the visible photo at scale 1 (object-fit: contain inside the item box). */
  function dims() {
    var im = img();
    var w = im.clientWidth, h = im.clientHeight;
    var nw = im.naturalWidth || Number(im.getAttribute('width')) || w;
    var nh = im.naturalHeight || Number(im.getAttribute('height')) || h;
    var r = Math.min(w / (nw || 1), h / (nh || 1)) || 1;
    return { cw: nw * r, ch: nh * r, vw: stage.clientWidth, vh: stage.clientHeight };
  }

  function box() {
    var b = dims();
    var rect = img().getBoundingClientRect();
    /* Center of the untransformed photo box, in viewport coordinates. */
    b.cx = rect.left + rect.width / 2 - z.x;
    b.cy = rect.top + rect.height / 2 - z.y;
    return b;
  }

  function limits(s, b) {
    return { x: Math.max(0, (s * b.cw - b.vw) / 2), y: Math.max(0, (s * b.ch - b.vh) / 2) };
  }

  function soft(v, lim) {
    if (v > lim) return lim + (v - lim) * 0.3;
    if (v < -lim) return -lim + (v + lim) * 0.3;
    return v;
  }

  /* Starting view: the product fills most of the screen, centered. */
  function fit() {
    var bd = bounds[cur];
    var im = img();
    if (!bd || !im || !im.naturalWidth) return { s: 1, x: 0, y: 0 };
    var b = dims();
    var bw = (bd[2] - bd[0]) * b.cw, bh = (bd[3] - bd[1]) * b.ch;
    if (!bw || !bh) return { s: 1, x: 0, y: 0 };
    var s = clamp(Math.min(b.vw * 0.9 / bw, (b.vh - 150) * 0.94 / bh), 1, 3);
    if (s < 1.08) return { s: 1, x: 0, y: 0 };
    var qx = ((bd[0] + bd[2]) / 2 - 0.5) * b.cw, qy = ((bd[1] + bd[3]) / 2 - 0.5) * b.ch;
    var L = limits(s, b);
    return { s: s, x: clamp(-s * qx, -L.x, L.x), y: clamp(-s * qy, -L.y, L.y) };
  }

  function apply(animate) {
    var im = img();
    if (!im) return;
    anim(animate);
    im.style.transform = 'translate3d(' + z.x + 'px,' + z.y + 'px,0) scale(' + z.s + ')';
    ui.classList.toggle('is-zoomed', zoomed());
    btnOut.disabled = z.s <= 1.01;
    btnIn.disabled = z.s >= maxS() - 0.01;
  }

  function reset(animate) {
    base = fit();
    z = { s: base.s, x: base.x, y: base.y };
    apply(animate);
  }

  /* Zoom to scale s keeping the viewport point (px, py) still. */
  function zoomAt(s, px, py, b, from) {
    from = from || z;
    var ox = px - b.cx, oy = py - b.cy;
    var k = s / from.s;
    z = { s: s, x: ox - k * (ox - from.x), y: oy - k * (oy - from.y) };
  }

  function settle(animate, px, py) {
    var b = box();
    var s = clamp(z.s, 1, maxS());
    if (s <= 1.01) { z = { s: 1, x: 0, y: 0 }; apply(animate); return; }
    if (s !== z.s) zoomAt(s, px == null ? b.cx : px, py == null ? b.cy : py, b);
    var L = limits(z.s, b);
    z.x = clamp(z.x, -L.x, L.x);
    z.y = clamp(z.y, -L.y, L.y);
    apply(animate);
  }

  function zoomBy(f, px, py) {
    interact();
    var b = box();
    var s = clamp(z.s * f, 1, maxS());
    if (px == null) { px = b.vw / 2 + stage.getBoundingClientRect().left; py = b.vh / 2 + stage.getBoundingClientRect().top; }
    zoomAt(s, px, py, b);
    settle(true);
  }

  function toggleAt(px, py) {
    interact();
    if (zoomed()) { reset(true); return; }
    var b = box();
    zoomAt(clamp(Math.max(STEP, base.s * 1.8), 1, maxS()), px, py, b);
    settle(true);
  }

  /* ---------- Gestures ---------- */
  function onDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    try { stage.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    pts[e.pointerId] = { x: e.clientX, y: e.clientY };
    begin(e);
  }

  function begin(e) {
    var n = count();
    anim(false);
    if (n === 1) {
      var p = pts[Object.keys(pts)[0]];
      g = { type: 'one', sx: p.x, sy: p.y, bx: z.x, by: z.y, t: Date.now(), moved: false, axis: null, mouse: e.pointerType === 'mouse', dx: 0, dy: 0, lx: p.x, lt: Date.now(), vx: 0 };
    } else if (n === 2) {
      var ab = two();
      g = { type: 'pinch', d0: Math.hypot(ab[0].x - ab[1].x, ab[0].y - ab[1].y) || 1, c0x: (ab[0].x + ab[1].x) / 2, c0y: (ab[0].y + ab[1].y) / 2, from: { s: z.s, x: z.x, y: z.y }, b: box(), moved: true };
      interact();
      lastTap = null;
    }
  }

  function onMove(e) {
    if (!pts[e.pointerId]) return;
    pts[e.pointerId] = { x: e.clientX, y: e.clientY };
    if (!g) return;
    if (g.type === 'pinch' && count() >= 2) {
      var ab = two();
      var d = Math.hypot(ab[0].x - ab[1].x, ab[0].y - ab[1].y) || 1;
      var cx = (ab[0].x + ab[1].x) / 2, cy = (ab[0].y + ab[1].y) / 2;
      var s = g.from.s * d / g.d0;
      var top = maxS();
      if (s > top) s = top + (s - top) * 0.25;
      if (s < 1) s = 1 - (1 - s) * 0.4;
      zoomAt(s, g.c0x, g.c0y, g.b, g.from);
      z.x += cx - g.c0x;
      z.y += cy - g.c0y;
      g.cx = cx; g.cy = cy;
      apply(false);
      return;
    }
    if (g.type !== 'one') return;
    var p = pts[e.pointerId];
    var dx = p.x - g.sx, dy = p.y - g.sy;
    var now = Date.now();
    if (now - g.lt > 0) { g.vx = (p.x - g.lx) / (now - g.lt); g.lx = p.x; g.lt = now; }
    g.dx = dx; g.dy = dy;
    if (!g.moved && Math.hypot(dx, dy) > 6) { g.moved = true; interact(); }
    if (!g.moved) return;
    if (zoomed() || g.pan) {
      ui.classList.add('is-drag');
      var b = box();
      var L = limits(Math.max(z.s, 1), b);
      z.x = soft(g.bx + dx, L.x);
      z.y = soft(g.by + dy, L.y);
      apply(false);
      return;
    }
    if (!g.axis) g.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
    if (g.axis === 'x') {
      var edge = (cur === 0 && dx > 0) || (cur === list.length - 1 && dx < 0);
      place(false, edge ? dx * 0.35 : dx);
    } else {
      var im = img();
      var down = Math.max(0, dy);
      im.style.transform = 'translate3d(' + z.x + 'px,' + (z.y + down) + 'px,0) scale(' + (z.s * (1 - Math.min(down / 1500, 0.15))) + ')';
      ui.style.backgroundColor = 'rgba(255,255,255,' + (1 - Math.min(down / 400, 0.6)) + ')';
    }
  }

  function onUp(e) {
    if (!pts[e.pointerId]) return;
    delete pts[e.pointerId];
    ui.classList.remove('is-drag');
    if (!g) return;
    if (g.type === 'pinch') {
      if (count() === 1) { begin(e); g.moved = true; g.pan = true; return; }
      var gx = g.cx, gy = g.cy;
      g = null;
      settle(true, gx, gy);
      return;
    }
    if (count() > 0) return;
    var gg = g;
    g = null;
    if (e.type === 'pointercancel') { ui.style.backgroundColor = ''; settle(true); place(true); return; }

    if (!gg.moved) {
      var now = Date.now();
      if (gg.mouse) { toggleAt(e.clientX, e.clientY); return; }
      if (lastTap && now - lastTap.t < 320 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 40) {
        lastTap = null;
        toggleAt(e.clientX, e.clientY);
      } else {
        lastTap = { t: now, x: e.clientX, y: e.clientY };
      }
      return;
    }
    lastTap = null;
    if (zoomed() || gg.pan || !gg.axis) { settle(true); return; }
    if (gg.axis === 'x') {
      var w = stage.clientWidth;
      if ((gg.dx < -w * 0.18 || gg.vx < -0.45) && cur < list.length - 1) go(cur + 1);
      else if ((gg.dx > w * 0.18 || gg.vx > 0.45) && cur > 0) go(cur - 1);
      else place(true);
      return;
    }
    ui.style.backgroundColor = '';
    if (gg.dy > 110) { ui.close(); return; }
    apply(true);
  }

  function onWheel(e) {
    e.preventDefault();
    interact();
    var b = box();
    var dy = e.deltaY * (e.deltaMode === 1 ? 33 : e.deltaMode === 2 ? 400 : 1);
    var f = Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0022));
    var s = clamp(z.s * f, 1, maxS());
    zoomAt(s, e.clientX, e.clientY, b);
    if (s <= 1.01) { z = { s: 1, x: 0, y: 0 }; apply(false); return; }
    var L = limits(z.s, b);
    z.x = clamp(z.x, -L.x, L.x);
    z.y = clamp(z.y, -L.y, L.y);
    apply(false);
  }

  function onKey(e) {
    if (e.target && e.target.closest && e.target.closest('button') && (e.key === 'Enter' || e.key === ' ')) return;
    var panning = zoomed();
    switch (e.key) {
      case 'ArrowRight': if (panning) { interact(); z.x -= 60; settle(true); } else go(cur + 1); break;
      case 'ArrowLeft': if (panning) { interact(); z.x += 60; settle(true); } else go(cur - 1); break;
      case 'ArrowUp': if (panning) { interact(); z.y += 60; settle(true); } else return; break;
      case 'ArrowDown': if (panning) { interact(); z.y -= 60; settle(true); } else return; break;
      case '+': case '=': btnIn.click(); break;
      case '-': case '_': zoomBy(1 / 1.6); break;
      case '0': reset(true); break;
      default: return;
    }
    e.preventDefault();
  }

  /* ---------- Open from the gallery ---------- */
  window.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var r = t.closest('[data-fnp]');
    if (!r || !r.querySelector('[data-fnp-zoom-dialog]')) return;
    if (typeof HTMLDialogElement === 'undefined') return;
    var btn = t.closest('[data-fnp-zoom]');
    var pic = !btn && t.closest('[data-fnp-slide] .fnp-frame img');
    if (!btn && !pic) return;
    e.preventDefault();
    e.stopPropagation();
    var start = btn ? (r.__fnp ? r.__fnp.index : 0) : Number(t.closest('[data-fnp-slide]').getAttribute('data-index')) || 0;
    open(r, start, btn || null);
  }, true);

  window.FnZoom = { open: open };
})();
